import type { ComponentProps } from 'react';
import { Button, Group, Pagination, Stack, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

export interface PaginationBarProps {
  page: number;
  totalPages: number;
  totalItems: number;
  rangeStart: number;
  rangeEnd: number;
  disabled?: boolean;
  onChange: (page: number) => void;
  /** Defaults match admin dashboard spacing. */
  mt?: ComponentProps<typeof Stack>['mt'];
  pt?: ComponentProps<typeof Stack>['pt'];
}

const prevNextButtonStyles = (theme: any) => ({
  root: {
    fontWeight: 500,
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
    '&:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors.dark[0], 0.1)
          : theme.fn.rgba(theme.black, 0.05),
    },
    '&[data-disabled]': {
      color: theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5],
      backgroundColor: 'transparent',
    },
  },
});

const paginationStyles = (theme: any) => ({
  control: {
    border: 'none',
    backgroundColor: 'transparent',
    fontWeight: 500,
    minWidth: 32,
    height: 32,
    fontSize: theme.fontSizes.sm,
    color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[6],
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover:not([data-disabled])': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors.dark[0], 0.1)
          : theme.fn.rgba(theme.black, 0.05),
      color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
    },
    '&[data-active]': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? `${theme.colors.dark[4]} !important`
          : `${theme.black} !important`,
      color: `${theme.white} !important`,
    },
    '&[data-active]:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? `${theme.colors.dark[3]} !important`
          : `${theme.fn.darken(theme.black, 0.1)} !important`,
      color: `${theme.white} !important`,
    },
    '&[data-disabled]': {
      opacity: 0.35,
      backgroundColor: 'transparent',
      border: 'none',
    },
  },
});

/**
 * Shared Previous / page numbers / Next bar used across admin and app list views.
 */
export function PaginationBar({
  page,
  totalPages,
  totalItems,
  rangeStart,
  rangeEnd,
  disabled = false,
  onChange,
  mt = 'xl',
  pt = 'md',
}: PaginationBarProps) {
  return (
    <Stack align="center" spacing={8} mt={mt} pt={pt}>
      <Group position="center" noWrap spacing="lg">
        <Button
          variant="subtle"
          color="dark"
          compact
          leftIcon={<IconChevronLeft size={14} />}
          disabled={disabled || page <= 1}
          onClick={() => onChange(page - 1)}
          styles={prevNextButtonStyles}
        >
          Previous
        </Button>

        <Pagination
          total={totalPages}
          value={page}
          onChange={onChange}
          disabled={disabled}
          color="dark"
          size="sm"
          radius="sm"
          withControls={false}
          styles={paginationStyles}
        />

        <Button
          variant="subtle"
          color="dark"
          compact
          rightIcon={<IconChevronRight size={14} />}
          disabled={disabled || page >= totalPages}
          onClick={() => onChange(page + 1)}
          styles={prevNextButtonStyles}
        >
          Next
        </Button>
      </Group>

      <Text size="xs" color="dimmed">
        Showing {rangeStart}&ndash;{rangeEnd} of {totalItems}
      </Text>
    </Stack>
  );
}
